/**
 * PERFORMANCE MAX CAMPAIGN WORKSPACE — /google-ads/<sales|local>/create/campaign
 *
 * Same shape as the App Promotion, YouTube and Search workspaces (step rail ·
 * goal strip · sticky live preview), tuned to Performance Max:
 *
 *   1. Campaign settings — store locations (Local), brand, name, bidding,
 *      budget, dates, locations and languages.
 *   2. Asset groups — MANY asset groups, each with text, images, logos,
 *      videos and optional audience signals (search themes, CRM lists).
 *   3. Review & save — grouped facts, every issue, and a live build log.
 *
 * Publishing is RESUMABLE: the campaign and each asset group are remembered
 * by draft id (and every uploaded image by its Google asset), so a rejection
 * can be fixed and re-published without creating anything twice.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, ChevronRight, Globe, Layers, Lock, MapPin, Plus, Settings, Sparkles, Store } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import {
  googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage, GoogleAdsUserList, PmaxBidding, PmaxReadiness,
} from '@/services/google-ads.service';
import { MEDIA_FOLDERS } from '@/services/uploads.service';
import { BackTo, errMsg, Label, pretty, Section, Switch, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { LanguagePicker, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';
import { hostOf, IMAGE_SPECS, ImageDraft, ImageGrid, isHttpUrl, MoneyInput, Pub, today, toMicros } from '@/components/google-ads/objectives/objectiveShared';
import {
  BudgetDates, BuildRow, Callout, ChipList, ChoiceCards, ConnectFirst, ConversionNote, CountedInput, Fact, FixList, GoalStrip, IntroBanner,
  PageSpinner, PublishPanel, RadioCard, RailStep, StrengthPill, TargetingFacts, WizardRail, WizardStepPills,
} from '@/components/google-ads/objectives/wizardChrome';
import {
  AssetGroupCard, AssetGroupDraft, assetGroupImageCount, assetGroupIssues, AssetGroupPreview, assetGroupStrength, assetGroupSummary, cloneAssetGroup,
  newAssetGroup, PMAX_BIDDING, PMAX_LIMITS, pmaxBiddingTitle, PmaxBrand, PmaxObjectiveConfig, publishAssetGroup, writeAssetInto,
} from './pmaxShared';

type StepKey = 'campaign' | 'groups' | 'review';
const STEPS: ReadonlyArray<RailStep<StepKey>> = [
  { key: 'campaign', label: 'Campaign settings', icon: Settings },
  { key: 'groups', label: 'Asset groups', icon: Layers },
  { key: 'review', label: 'Review & save', icon: CheckCircle2 },
];
type Patch<T> = Partial<T> | ((cur: T) => Partial<T>);
/** Pseudo-choice for "every location in the account-level Business Profile sync" (no campaign link). */
const ALL_LOCATIONS = '__all__';

export default function PmaxCampaignWizard({ objective: cfg }: { objective: PmaxObjectiveConfig }) {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';
  const [step, setStep] = useState<StepKey>('campaign');

  // ── campaign settings ──
  const [website, setWebsite] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [brandLogo, setBrandLogo] = useState<ImageDraft[]>([]);
  const [name, setName] = useState(`${cfg.namePrefix} - ${today()}`);
  const [bidding, setBidding] = useState<PmaxBidding>('MAXIMIZE_CONVERSIONS');
  const [targetCpa, setTargetCpa] = useState('');
  const [targetRoas, setTargetRoas] = useState('');
  const [budget, setBudget] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [locations, setLocations] = useState<GoogleAdsGeoSuggestion[]>([]);
  const [languages, setLanguages] = useState<GoogleAdsLanguage[]>([]);
  const [chosenSets, setChosenSets] = useState<string[]>([]);
  // Google recommends turning it off for store-goal campaigns; on (its default) for everything else.
  const [urlExpansion, setUrlExpansion] = useState(!cfg.storeGoals);

  // ── account facts ──
  const [readiness, setReadiness] = useState<PmaxReadiness | null>(null);
  const [audiences, setAudiences] = useState<GoogleAdsUserList[] | null>(null);
  const [takenNames, setTakenNames] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.performanceMax.readiness(accountUid)
      .then((r) => {
        setReadiness(r);
        if (!cfg.storeGoals || !r.locationSets?.length) return;
        setChosenSets(r.locationSets.some((x) => x.type === 'LOCATION_SYNC') ? [ALL_LOCATIONS] : [r.locationSets[0].id]);
      })
      .catch(() => setReadiness({ conversions: { trackingStatus: null, enabledConversionActions: null, conversionBiddingAllowed: null }, locationSets: null }));
    googleAdsService.listAudiences(accountUid).then(setAudiences).catch(() => setAudiences([]));
    googleAdsService.listCampaigns(accountUid)
      .then((l) => setTakenNames(new Set(l.filter((c) => c.status !== 'REMOVED').map((c) => c.name.trim().toLowerCase()))))
      .catch(() => undefined);
  }, [accountUid, cfg.storeGoals]);
  const locationSets = readiness?.locationSets ?? null;
  // The account-level Business Profile sync (LOCATION_SYNC) covers every location without a campaign link;
  // location GROUPS are what a campaign links to pick a subset.
  const hasSync = !!locationSets?.some((x) => x.type === 'LOCATION_SYNC');
  const locationGroups = (locationSets ?? []).filter((x) => x.type !== 'LOCATION_SYNC');
  const linkedGroups = chosenSets.filter((id) => id !== ALL_LOCATIONS);
  const pickSet = (id: string) => setChosenSets((cur) => {
    if (id === ALL_LOCATIONS) return [ALL_LOCATIONS];
    const rest = cur.filter((x) => x !== ALL_LOCATIONS);
    return rest.includes(id) ? rest.filter((x) => x !== id) : [...rest, id];
  });

  // ── structure ──
  const siteUrl = isHttpUrl(website) ? website.trim() : '';
  const brand = useMemo<Partial<PmaxBrand>>(() => ({ finalUrl: siteUrl, businessName: businessName.trim(), logos: brandLogo.filter((l) => l.url) }), [siteUrl, businessName, brandLogo]);
  const first = useMemo(() => newAssetGroup(1), []);
  const [groups, setGroups] = useState<AssetGroupDraft[]>([first]);
  const [openId, setOpenId] = useState(first.id);
  const [activeId, setActiveId] = useState(first.id);
  const patch = (gid: string, p: Patch<AssetGroupDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...(typeof p === 'function' ? p(g) : p) } : g)));
  const applyBrand = () => setGroups((gs) => gs.map((g) => ({
    ...g,
    finalUrl: g.finalUrl || brand.finalUrl || '',
    businessName: g.businessName || brand.businessName || '',
    logos: g.logos.length ? g.logos : (brand.logos ?? []).map((i) => ({ ...i })),
  })));

  // ── publish ──
  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [signalNotes, setSignalNotes] = useState<Record<string, string>>({});
  const [goalNote, setGoalNote] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [finished, setFinished] = useState(false);
  const madeRef = useRef<{ campaign?: string; groups: Record<string, string> }>({ groups: {} });
  const campaignLocked = !!madeRef.current.campaign;

  // ── validation ──
  const campaignIssues = useMemo(() => {
    const i: string[] = [];
    if (website.trim() && !siteUrl) i.push('The website must be a full address starting with https://');
    if (businessName.length > PMAX_LIMITS.businessName) i.push(`The business name must be ${PMAX_LIMITS.businessName} characters or fewer.`);
    if (!name.trim()) i.push('Campaign name is required.');
    else if (!campaignLocked && takenNames.has(name.trim().toLowerCase())) i.push(`A campaign named "${name.trim()}" already exists on this account — Google needs a new name.`);
    if (toMicros(budget) <= 0) i.push('Enter a daily budget.');
    if (start && end && end < start) i.push('The end date is before the start date.');
    if (bidding === 'MAXIMIZE_CONVERSION_VALUE' && targetRoas && !(Number(targetRoas) > 0)) i.push('Target ROAS must be above zero.');
    if (cfg.storeGoals && locationSets !== null && locationSets.length === 0) i.push('Store goals need a Google Business Profile linked to this Google Ads account.');
    if (cfg.storeGoals && locationSets && locationSets.length > 0 && chosenSets.length === 0) i.push('Pick the store locations to promote.');
    if (brandLogo.some((l) => !l.url)) i.push('The logo is still uploading.');
    return i;
  }, [website, siteUrl, businessName, name, campaignLocked, takenNames, budget, start, end, bidding, targetRoas, cfg.storeGoals, locationSets, chosenSets, brandLogo]);

  const groupIssues = useMemo(() => {
    const i: string[] = [];
    if (!groups.length) i.push('Add at least one asset group.');
    groups.forEach((g, n) => { const gi = assetGroupIssues(g); if (gi.length) i.push(`${g.name.trim() || `Asset group ${n + 1}`}: needs ${gi.join(', ')}.`); });
    return i;
  }, [groups]);

  const allIssues = [...campaignIssues, ...groupIssues];
  const issuesFor = (k: StepKey) => (k === 'campaign' ? campaignIssues.length : k === 'groups' ? groupIssues.length : 0);
  const active = groups.find((g) => g.id === activeId) ?? groups[0] ?? null;
  const imageCount = groups.reduce((n, g) => n + assetGroupImageCount(g), 0);
  const biddingSub = bidding === 'MAXIMIZE_CONVERSIONS'
    ? (toMicros(targetCpa) > 0 ? `Target CPA ${targetCpa} ${currency}` : 'No target — Google learns first')
    : (Number(targetRoas) > 0 ? `Target ROAS ${Math.round(Number(targetRoas) * 100)}%` : 'No target — Google learns first');
  const chosenSetRows = locationGroups.filter((s) => chosenSets.includes(s.id));
  const storeLabel = chosenSets.includes(ALL_LOCATIONS) ? 'All Business Profile locations' : chosenSetRows.map((s) => s.name || s.id).join(', ');
  const lowBudget = bidding === 'MAXIMIZE_CONVERSIONS' && toMicros(targetCpa) > 0 && toMicros(budget) > 0 && Number(budget) < Number(targetCpa) * 3;

  // ── publish (resumable) ──
  const publish = async () => {
    if (!accountUid || allIssues.length) return;
    setPublishing(true); setPubError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    const made = madeRef.current;
    try {
      if (!made.campaign) {
        mark('campaign', 'running');
        try {
          const r = await googleAdsService.performanceMax.createCampaign(accountUid, {
            name: name.trim(), dailyBudgetMicros: toMicros(budget), status: 'PAUSED', bidding, objective: cfg.key,
            targetCpaMicros: bidding === 'MAXIMIZE_CONVERSIONS' && toMicros(targetCpa) > 0 ? toMicros(targetCpa) : undefined,
            targetRoas: bidding === 'MAXIMIZE_CONVERSION_VALUE' && Number(targetRoas) > 0 ? Number(targetRoas) : undefined,
            startDate: start || undefined, endDate: end || undefined,
            geoTargetIds: locations.length ? locations.map((l) => l.id) : undefined,
            languageIds: languages.length ? languages.map((l) => l.id) : undefined,
            locationAssetSetIds: cfg.storeGoals && linkedGroups.length ? linkedGroups : undefined,
            finalUrlExpansion: urlExpansion,
          });
          made.campaign = r.campaignId;
          if (r.goals) setGoalNote(r.goals.error ? `goals not switched — ${r.goals.error}` : r.goals.biddable.length ? `bidding on ${r.goals.biddable.map((g) => pretty(g.split('/')[0])).join(', ')}` : (r.goals.note ?? ''));
        } catch (e) { mark('campaign', 'failed'); setPubError(`Campaign: ${errMsg(e)}`); return; }
        mark('campaign', 'done');
      }
      for (const g of groups) {
        if (made.groups[g.id]) continue;
        mark(g.id, 'running');
        try {
          const r = await publishAssetGroup(accountUid, made.campaign!, g, (field, id, rn) => patch(g.id, writeAssetInto(field, id, rn)));
          made.groups[g.id] = r.assetGroupId;
          if (r.signals?.error) {
            setSignalNotes((m) => ({ ...m, [g.id]: `signals not added — ${r.signals!.error}` }));
            toast.error(`${g.name.trim()}: created, but its audience signals were refused — ${r.signals.error}`);
          } else if (r.signals?.added) setSignalNotes((m) => ({ ...m, [g.id]: `${r.signals!.added} signal${r.signals!.added === 1 ? '' : 's'}` }));
        } catch (e) { mark(g.id, 'failed'); setPubError(`Asset group "${g.name.trim()}": ${errMsg(e)}`); return; }
        mark(g.id, 'done');
      }
      setFinished(true);
      toast.success(`${cfg.title} campaign published — everything is paused, so nothing spends until you enable it.`);
      setTimeout(() => navigate(cfg.base), 1200);
    } finally { setPublishing(false); }
  };

  const buildRows: BuildRow[] = [
    { key: 'campaign', label: `Campaign — ${name || 'untitled'}${cfg.storeGoals && linkedGroups.length ? ` + ${linkedGroups.length} store location group${linkedGroups.length === 1 ? '' : 's'}` : ''}`, level: 0, state: pub.campaign,
      detail: cfg.storeGoals ? (goalNote ?? 'then switch its goals to store visits / local actions') : undefined },
    ...groups.map((g, n): BuildRow => ({
      key: g.id, label: `Asset group — ${g.name.trim() || `Asset group ${n + 1}`}`, level: 1, state: pub[g.id],
      detail: signalNotes[g.id] ?? assetGroupSummary(g),
    })),
  ];
  const Icon = cfg.icon;

  return (
    <>
      <Topbar title={`New ${cfg.title} campaign`} breadcrumb={`Google Ads / ${cfg.title}`}
        description="Performance Max campaign and its asset groups — then save everything paused"
        back={<BackTo to={cfg.base} label={`Back to ${cfg.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="Performance Max campaigns" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <WizardRail steps={STEPS} step={step} onStep={setStep} issues={issuesFor} summary={(
              <>
                <div>{groups.length} asset group{groups.length === 1 ? '' : 's'}</div>
                <div>{imageCount} image{imageCount === 1 ? '' : 's'}</div>
                {cfg.storeGoals && <div>{chosenSets.includes(ALL_LOCATIONS) ? 'All store locations' : linkedGroups.length ? `${linkedGroups.length} location group${linkedGroups.length === 1 ? '' : 's'}` : 'No store locations'}</div>}
                {active && <div className="flex items-center gap-1.5 pt-1">Strength <StrengthPill s={assetGroupStrength(active)} /></div>}
              </>
            )} />

            <div className="min-w-0 flex-1 space-y-4">
              <WizardStepPills steps={STEPS} step={step} onStep={setStep} />
              <GoalStrip items={[
                { label: 'Goal', value: cfg.goal },
                { label: 'Type', value: cfg.storeGoals ? 'Performance Max · store goals' : 'Performance Max' },
                { label: 'Served via', value: cfg.storeGoals ? 'Maps · Search · YouTube · Display' : 'Search · YouTube · Display · Gmail · Discover · Maps' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              {/* ── STEP 1: campaign ── */}
              {step === 'campaign' && (
                <>
                  {campaignLocked && (
                    <Callout icon={Lock}>The campaign already exists on Google, so its settings are locked. Fix the failing asset group and publish again.</Callout>
                  )}
                  <fieldset disabled={campaignLocked} className="min-w-0 space-y-4">
                    <IntroBanner icon={Icon}
                      title={cfg.storeGoals ? 'Performance Max for store visits' : 'Performance Max — one campaign across every Google surface'}
                      points={cfg.storeGoals
                        ? ['Linked Business Profile locations', 'Asset groups', 'Maps, Search, YouTube & Display', 'Starts paused']
                        : ['Asset groups instead of ads', 'Search, YouTube, Display, Gmail, Discover & Maps', 'Audience signals', 'Starts paused']}>
                      {cfg.storeGoals
                        ? 'Google promotes your locations — directions, calls and visits — across Maps, Search, YouTube and Display, using the text and images in your asset groups.'
                        : 'Give Google your text, images and videos once; it builds every ad format and finds the customers most likely to buy across all of Google.'}
                    </IntroBanner>

                    {cfg.storeGoals && (
                      <Section title="Store locations" hint="Google optimises for visits to these locations — they come from the Business Profile linked to this ad account.">
                        {locationSets === null ? (
                          <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
                        ) : locationSets.length === 0 ? (
                          <Callout tone="warn" icon={Store}>
                            <p className="font-semibold text-text-primary">No linked Business Profile found.</p>
                            <p className="mt-1">In Google Ads open <strong>Tools → Data manager → Linked accounts → Google Business Profile</strong>, link the profile that owns your locations, then reload this page.</p>
                          </Callout>
                        ) : (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                              {hasSync && (
                                <RadioCard icon={Store} on={chosenSets.includes(ALL_LOCATIONS)} onClick={() => pickSet(ALL_LOCATIONS)}
                                  title="All Business Profile locations" desc="Every location synced from your linked Business Profile" />
                              )}
                              {locationGroups.map((s) => (
                                <RadioCard key={s.id} multi icon={MapPin} on={chosenSets.includes(s.id)} title={s.name || `Location group ${s.id}`}
                                  desc={`Location group · ${pretty(s.type).replace(/ Location Group$/i, '')}`} onClick={() => pickSet(s.id)} />
                              ))}
                            </div>
                            <Callout tone="muted" icon={MapPin}>
                              Google measures store visits in the UAE and Türkiye (among MENA markets) once enough people visit. Elsewhere it
                              optimises for local actions — direction and call clicks — so this campaign still works across the Gulf.
                            </Callout>
                          </div>
                        )}
                      </Section>
                    )}

                    <Section title="Your brand" hint="Every new asset group starts with these — you can still change them per asset group.">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label>Website</Label>
                          <Input className="mt-1.5" iconLeft={<Globe size={14} />} placeholder="https://yourbusiness.com" value={website}
                            onChange={(e) => setWebsite(e.target.value)} onBlur={applyBrand} />
                          {siteUrl && <p className="mt-1 text-2xs text-success">{hostOf(siteUrl)} — asset groups link here unless you give one its own page.</p>}
                        </div>
                        <div>
                          <Label>Business name</Label>
                          <CountedInput className="mt-1.5" value={businessName} maxLen={PMAX_LIMITS.businessName} placeholder="Shown on every ad"
                            onChange={setBusinessName} ariaLabel="Business name" />
                        </div>
                      </div>
                      <div className="mt-4 max-w-md">
                        <ImageGrid label="Square logo" spec={IMAGE_SPECS.logo} images={brandLogo} max={1} optional folder={MEDIA_FOLDERS.googlePmax}
                          onChange={(f) => setBrandLogo((cur) => f(cur))} />
                      </div>
                    </Section>

                    <Section title="Campaign name">
                      <Input value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
                    </Section>

                    <Section title="Landing pages" hint="Where people land after they click.">
                      <div className="flex items-start gap-3 rounded-xl border border-border px-3.5 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-text-primary">Final URL expansion</div>
                          <p className="mt-0.5 text-2xs leading-4 text-text-muted">
                            Lets Google send people to a more relevant page of your website and write matching headlines from it.
                            {cfg.storeGoals ? ' Google recommends keeping it off for store-visit campaigns.' : ' Turn it off if every click must land on the URLs you give.'}
                          </p>
                        </div>
                        <Switch checked={urlExpansion} label="Final URL expansion" onCheckedChange={setUrlExpansion} />
                      </div>
                    </Section>

                    <Section title="Bidding" hint="Performance Max is always automated — you choose what it optimises for.">
                      <ConversionNote readiness={readiness?.conversions ?? null}
                        blockedText="This account has no conversion tracking yet. Google still accepts the campaign, but it has nothing to optimise for until you add a conversion action in Google Ads → Goals → Conversions." />
                      <ChoiceCards options={PMAX_BIDDING} value={bidding} onChange={setBidding} recommended="MAXIMIZE_CONVERSIONS" />
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {bidding === 'MAXIMIZE_CONVERSIONS' ? (
                          <div>
                            <Label>Target CPA <span className="font-normal text-text-muted">(optional)</span></Label>
                            <div className="mt-1.5"><MoneyInput value={targetCpa} onChange={setTargetCpa} currency={currency} placeholder="Let Google decide" /></div>
                            <p className="mt-1 text-2xs text-text-muted">Leave blank for the first weeks — Google learns faster without a target.</p>
                          </div>
                        ) : (
                          <div>
                            <Label>Target ROAS <span className="font-normal text-text-muted">(optional — 4 = 400%)</span></Label>
                            <Input className="mt-1.5" inputMode="decimal" value={targetRoas} onChange={(e) => setTargetRoas(e.target.value)} placeholder="e.g. 4" />
                          </div>
                        )}
                      </div>
                    </Section>

                    <Section title="Budget and dates">
                      <BudgetDates budget={budget} onBudget={setBudget} start={start} onStart={setStart} end={end} onEnd={setEnd} currency={currency}
                        budgetHint="Performance Max needs room to learn — Google suggests at least 3× your target cost per conversion." />
                      {lowBudget && (
                        <Callout tone="warn" className="mt-3">
                          The daily budget is under 3× the target CPA ({targetCpa} {currency}). Google may barely serve the campaign while it learns — raise the budget to about {(Number(targetCpa) * 3).toFixed(0)} {currency}, or remove the target.
                        </Callout>
                      )}
                    </Section>

                    <Section title="Locations" hint="Searched live from Google — countries, regions and cities. Empty = everywhere Google serves.">
                      <LocationSearch accountUid={accountUid} value={locations} onChange={setLocations} />
                    </Section>

                    <Section title="Languages" hint="Languages your customers use Google in. Empty = all languages.">
                      <LanguagePicker accountUid={accountUid} value={languages} onChange={setLanguages} />
                    </Section>
                  </fieldset>

                  <div className="flex justify-end">
                    <Button onClick={() => { applyBrand(); setStep('groups'); }} iconRight={<ChevronRight size={15} />}>Next: Asset groups</Button>
                  </div>
                </>
              )}

              {/* ── STEP 2: asset groups ── */}
              {step === 'groups' && (
                <>
                  <Callout icon={Sparkles}>
                    An asset group is one theme — a product line, a service or an audience. Google builds every ad format from it, so give it
                    variety: many headlines, several image shapes and a video. Images of any shape are cropped to Google's ratios automatically.
                  </Callout>

                  {groups.map((g, n) => (
                    <AssetGroupCard key={g.id} group={g} index={n} open={openId === g.id} onToggle={() => setOpenId(openId === g.id ? '' : g.id)}
                      state={pub[g.id]} made={!!madeRef.current.groups[g.id]} busy={publishing} onChange={(p) => patch(g.id, p)}
                      focused={activeId === g.id} onFocus={() => setActiveId(g.id)} audiences={audiences}
                      onDuplicate={() => { const c = cloneAssetGroup(g); setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; }); setOpenId(c.id); setActiveId(c.id); }}
                      onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined} />
                  ))}

                  <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={publishing}
                    onClick={() => { const g = newAssetGroup(groups.length + 1, brand); setGroups((gs) => [...gs, g]); setOpenId(g.id); setActiveId(g.id); }}>
                    Add another asset group
                  </Button>

                  <div className="flex justify-between">
                    <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                    <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>Next: Review</Button>
                  </div>
                </>
              )}

              {/* ── STEP 3: review ── */}
              {step === 'review' && (
                <>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_1fr]">
                    <Section title="Campaign" hint="Everything Google will receive for the campaign itself."
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('campaign')}>Edit</Button>}>
                      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                        <Fact label="Name" value={name || '—'} wide />
                        <Fact label="Goal" value={cfg.title} sub={cfg.goal} />
                        <Fact label="Bidding" value={pmaxBiddingTitle(bidding)} sub={biddingSub} />
                        <Fact label="Daily budget" value={budget ? `${budget} ${currency}` : '—'}
                          sub={toMicros(budget) > 0 ? `≈ ${(Number(budget) * 30.4).toFixed(0)} ${currency} / month` : undefined} />
                        <Fact label="Schedule" value={start ? `${start} → ${end || 'no end'}` : `Starts when enabled${end ? ` → ends ${end}` : ' · no end date'}`} />
                        {cfg.storeGoals && <Fact label="Store locations" value={storeLabel || 'None linked'} sub="Goals switched to store visits / local actions on publish" wide />}
                        <Fact label="Final URL expansion" value={urlExpansion ? 'On — Google may pick other pages' : 'Off — only your URLs'} />
                        {siteUrl && <Fact label="Website" value={siteUrl} wide />}
                      </div>
                      <TargetingFacts locations={locations.map((l) => l.name)} languages={languages.map((l) => l.name)} />
                    </Section>

                    <Section title="Asset groups" hint={`${groups.length} asset group${groups.length === 1 ? '' : 's'} · ${imageCount} image${imageCount === 1 ? '' : 's'}`}
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('groups')}>Edit</Button>}>
                      <div className="space-y-2.5">
                        {groups.map((g, n) => {
                          const imgs = [...g.marketingImages, ...g.squareImages, ...g.portraitImages].filter((i) => i.url).slice(0, 5);
                          return (
                            <button key={g.id} type="button" onClick={() => setActiveId(g.id)}
                              className="block w-full rounded-lg border border-border p-2.5 text-left transition-colors hover:bg-surface-alt/60">
                              <div className="flex items-center gap-2">
                                <Layers size={13} className="shrink-0 text-text-muted" />
                                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{g.name.trim() || `Asset group ${n + 1}`}</span>
                                <StrengthPill s={assetGroupStrength(g)} />
                              </div>
                              <div className="mt-1 pl-5 text-2xs text-text-muted">{assetGroupSummary(g)}</div>
                              {imgs.length > 0 && (
                                <div className="mt-2 flex gap-1.5 pl-5">
                                  {imgs.map((im) => <img key={im.id} src={im.url} alt="" className="h-9 w-12 rounded object-cover" />)}
                                </div>
                              )}
                              {(g.searchThemes.length > 0 || g.audienceLists.length > 0) && (
                                <div className="mt-2 pl-5">
                                  <ChipList items={[...g.searchThemes, ...g.audienceLists.map((rn) => audiences?.find((a) => a.resourceName === rn)?.name ?? 'Customer list')]} empty="" max={6} />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </Section>
                  </div>

                  <FixList groups={[{ step: 'campaign' as StepKey, issues: campaignIssues }, { step: 'groups' as StepKey, issues: groupIssues }]} onJump={setStep} />

                  <PublishPanel
                    hint="Creates everything on Google in order — campaign, image and video assets, asset groups, then their audience signals. It all starts paused, so nothing spends until you enable the campaign."
                    rows={buildRows} error={pubError} publishing={publishing} blocked={allIssues.length > 0} finished={finished}
                    onPublish={publish} onCancel={() => navigate(cfg.base)} />
                </>
              )}
            </div>

            {/* live preview */}
            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><AssetGroupPreview group={active} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
